
<template>
<div>
  <chc-modal ref="addBuyerModal" title="Add new buyer">
    <div slot="body">
      <buyer-add
        :companyName="addBuyerCompanyName"
        v-on:refresh="refresh"
        v-on:close="addBuyerHide">
        ></buyer-add>
    </div>
  </chc-modal>
  <section class="vehicle-processing">
    <div class="tabbed-content">
      <div class="valuation-wrapper">
        <h2>Valuation</h2>
        <div class="valuation">
          <h3>Kelly Blue Book</h3>
          <dl>
            <dt class="data-label">Trade In</dt>
            <dd class="data-content">{{ data.kbb_trade_in_cents }}&nbsp;</dd>
          </dl>
          <dl>
            <dt class="data-label">Auction</dt>
            <dd class="data-content">{{ data.kbb_auction_cents }}&nbsp;</dd>
          </dl>
          <dl>
            <dt class="data-label">Lending</dt>
            <dd class="data-content">{{ data.kbb_lending_cents }}&nbsp;</dd>
          </dl>
        </div>
        <div class="valuation" v-if="data.kbb_error_msg && data.kbb_error_msg.length > 0">
          <span class="error-message">Kelley Blue Book Error Message: {{ data.kbb_error_msg }}</span>
        </div>

        <div class="valuation">
          <h3>Peddle</h3>
          <dl>
            <dt class="data-label">Offer</dt>
            <dd class="data-content">{{ data.peddle_onspot_cents }}&nbsp;</dd>
          </dl>
          <dl v-if="data.peddle_drop_off_cents">
            <dt class="data-label">Drop Off Offer <span class="error-message">deprecated</span></dt>
            <dd class="data-content">{{ data.peddle_drop_off_cents }}&nbsp;</dd>
          </dl>
          <dl v-if="data.peddle_maximum_cents">
            <dt class="data-label">Maximum Asking Price <span class="error-message">deprecated</span></dt>
            <dd class="data-content">{{ data.peddle_maximum_cents }}&nbsp;</dd>
          </dl>
        </div>
        <div class="valuation" v-if="data.peddle_error_msg && data.peddle_error_msg.length > 0">
          <span class="error-message">Peddle Error Message: {{ data.peddle_error_msg }}</span>
        </div>

        <div class="valuation">
          <h3>Copart</h3>
          <div class="field">
            <chc-input v-model="data.copart_proquote_cents" label="ProQuote Value" format="currency" class="valuation-input"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.copart_average_cents" label="Average Value" format="currency" class="valuation-input"></chc-input>
          </div>
        </div>

        <h3>Salvage Quotes</h3>
        <div class="valuation salvage-valuation" v-for="(quote, index) in data.salvage_quotes" v-bind:key="index">
          <div class="field">
            <chc-input class="valuation-input" v-model="quote.buyer" label="Buyer" format="combo"
                     :autoCompleteValues="$store.state.summary.buyers" ref="quotesBuyerCombo" @enter="addBuyerShow(index, $event)"></chc-input>
          </div>
          <div class="field">
            <chc-input class="valuation-input" v-model="quote.amount" label="Salvage Quote Amount" format="currency"></chc-input>
          </div>
          <div class="field">
            <chc-input class="valuation-input" v-model="quote.date" label="Salvage Quote Date" type="date"></chc-input>
          </div>
        </div>

        <div class="salvage-valuation">
          <p><a v-on:click="addBuyerShow">+ New Buyer</a></p>
          <p><a v-on:click="addSalvageQuote">+ Add Another Quote</a></p>
        </div>
      </div>

      <hr>

      <div class="buyer-wrapper">
        <h2>Buyer &amp; Towing</h2>
      </div>
      <div class="columns">
        <div class="col">
          <div class="field">
            <chc-input v-model="data.buyer" label="Buyer" format="combo" @combo-select="selectedBuyer"
                       :error="errors.buyer"
                       :autoCompleteValues="$store.state.summary.buyersWithTow" ref="buyerCombo"></chc-input>
          </div>
          <div class="field">
            <span v-if="sendBuyerErrors" class="error-message error-block">
              <p>{{ sendBuyerErrors.message }}</p>
              <p>Could not send to external API. Errors are listed above.</p>
            </span>
            <input v-if="!data.buyer_vehicle_info_sent_date" @click="sendBuyerVehicleInfo" type="submit" class="btn"
                  value="Send Buyer Vehicle Information" :disabled="!data.buyer || !data.can_send_buyer_info || isSendingToBuyer">
            <dl class="data-sent" v-if="data.buyer_vehicle_info_sent_date && !sendBuyerErrors && !isSendingToBuyer">
              <dt class="data-label">Buyer Info Sent</dt>
              <dd class="data-content"> {{ data.buyer_vehicle_info_sent_date }} </dd>
            </dl>
            <a @click="sendBuyerVehicleInfo" v-if="data.buyer_vehicle_info_sent_date" :disabled="!data.buyer || !data.can_send_buyer_info">Resend Buyer Info</a>
          </div>
        </div>

        <div class="col">
          <div class="field">
            <chc-input v-model="data.towing_company_id" label="Towing Company" format="combo"
                     :error="errors.towing_company_id"
                     :autoCompleteValues="$store.state.summary.towing_companies" ref="towingCompanyCombo"></chc-input>
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="col">
          <h3>Tow Pickup <a @click="copyDonorAddress">Copy Donor Address</a></h3>
          <div class="field">
            <chc-input v-model="data.pickup_business_name" label="Business Name" ref="businessName" :error="errors.pickup_business_name"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.pickup_contact_name" label="Contact Name" ref="contactName" :error="errors.pickup_contact_name"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.pickup_phone" label="Phone" :error="errors.pickup_phone" ref="pickupPhone"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.pickup_address_street" label="Address" :error="errors.pickup_address_street" ref="pickupAddressStreet"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.pickup_address_city" label="City" :error="errors.pickup_address_city" ref="pickupAddressCity"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.pickup_address_state" label="State" format="combo"
                     :autoCompleteValues="$store.state.constants.us_states_list" :error="errors.pickup_address_state" ref="pickupAddressState"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.pickup_address_zip" label="Zip" format="zip" :error="errors.pickup_address_zip" ref="pickpuAddressZip"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.pickup_location" label="Vehicle Location" :error="errors.pickup_location"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.pickup_special_instructions" label="Special Instructions" :error="errors.pickup_special_instructions"></chc-input>
          </div>
          <div class="field left">
            <input v-if="!data.vdr_sent_date" @click="sendDonorVdr" type="submit" class="btn" value="Send Donor VDR" :disabled="!canSendVdr || !data.donor_has_email">
            <dl class="data-sent" v-if="data.vdr_sent_date">
              <dt class="data-label">VDR Sent</dt>
              <dd class="data-content"> {{ data.vdr_sent_date }} </dd>
              <a @click="sendDonorVdr" v-if="canSendVdr">Resend VDR</a>
              <a @click="downloadVdr"> <span><i class="far fa-file-pdf"></i></span> Download VDR</a>
            </dl>
          </div>
          <div v-if="!canSendVdr" class="field">
            <span class="error-message">Cannot send VDR until towing information is complete</span>
          </div>
          <div v-if="canSendVdr && !data.donor_has_email" class="field">
            <span class="error-message">No email in system for donor</span>
          </div>
          <div v-if="hasMailErrors && errors.send_vdr" class="field">
            <span class="error-message">{{errors.send_vdr}}</span>
          </div>
        </div>

        <div class="col">
          <h3>Tow Destination <a @click="copyBuyerAddress">Copy Buyer Address</a></h3>
          <div class="field">
            <chc-input v-model="data.destination_address_street" label="Address" :error="errors.destination_address_street" ref="destinationAddressStreet"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.destination_address_city" label="City" :error="errors.destination_address_city" ref="destinationAddressCity"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.destination_address_state" label="State" format="combo"
                     :autoCompleteValues="$store.state.constants.us_states_list" :error="errors.destination_address_state" ref="destinationAddressState"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.destination_address_zip" label="Zip" format="zip" :error="errors.destination_address_zip" ref="destinationAddressZip"></chc-input>
          </div>
          <div class="field">
            <input v-if="!data.towing_request_sent_date" @click="sendTowingRequest" type="submit" class="btn" value="Send Towing Request" :disabled="!canSendTowRequest">
            <dl class="data-sent" v-if="data.towing_request_sent_date">
              <dt class="data-label">Towing Request Sent</dt>
              <dd class="data-content"> {{ data.towing_request_sent_date }} </dd>
              <a @click="sendTowingRequest" v-if="canSendTowRequest">Resend Towing Request</a>
              <a @click="downloadTowingOrder"><span class="far fa-file-pdf"></span> Download Towing Order</a>
            </dl>
          </div>
          <div v-if="hasMailErrors && errors.send_tow" class="field">
            <span class="error-message">{{errors.send_tow}}</span>
          </div>
          <div class="field">
            <chc-input v-model="data.request_received" label="Towing Request Received" type="date" :error="errors.request_received"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.scheduled_pickup" label="Scheduled Tow Date" type="date" :error="errors.scheduled_pickup"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.tow_time" label="Tow Time" :error="errors.tow_time" type="time"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.title_location" label="Title Location" :error="errors.title_location"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.key_location" label="Key Location" :error="errors.key_location"></chc-input>
          </div>
          <div class="field">
            <chc-input v-model="data.actual_pickup" label="Actual Tow Date" type="date" :error="errors.actual_pickup"></chc-input>
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="col">
          <div class="field">
            <span v-if="hasErrors" class="error-message error-block">
              There were errors processing the form. Please fix the fields above.
            </span>
          </div>
          <div class="field buttons left submit-section">
            <input type="submit" @click="submit" class="btn" value="Save Changes">
            <transition name="fade">
              <p v-if="saved" class="success-message">Changes Saved</p>
            </transition>
          </div>
        </div>
      </div>
    </div>

  </section>
</div>
</template>

<script>
import store from '@/store'
import helpers from '@/helpers'
import BuyerAdd from '@/components/Buyer/BuyerAdd'

export default {
  name: 'VehicleProcessing',
  data () {
    return {
      errors: {},
      saved: false,
      hasErrors: false,
      hasMailErrors: false,
      sendBuyerErrors: null,
      isSendingToBuyer: false,
      addBuyerCompanyName: null,
      addBuyerSalvageIndex: 0,
    }
  },
  props: ['vehicleId', 'data', 'donorData', 'refNum'],
  components: {
    BuyerAdd
  },
  methods: {
    addSalvageQuote () {
      this.data.salvage_quotes.push({
        buyer: null,
        amount: null,
        date: null
      })
    },
    addBuyerShow (index, ref) {
      if (ref && ref.selectedValidOption) return
      if (ref) this.addBuyerCompanyName = ref.value
      if (index) this.addBuyerSalvageIndex = index
      else this.addBuyerSalvageIndex = this.salvage_quotes.length - 1
      this.$refs.addBuyerModal.show()
    },
    addBuyerHide () {
      this.addBuyerCompanyName = null
      this.$refs.addBuyerModal.hide()
    },
    refresh (buyer) {
      Promise.all([
        store.dispatch('syncSummary'),
      ]).catch(() => {
      }).then(() => {
        this.data.salvage_quotes[this.addBuyerSalvageIndex].buyer = buyer.buyer.id
      })
    },
    formatBuyerId () {
      if (this.data.buyer && typeof this.data.buyer === 'object') this.data.buyer_id = this.data.buyer.value
      else if (this.data.buyer) this.data.buyer_id = this.data.buyer
    },
    copyDonorAddress () {
      this.$refs.businessName.forceUpdateValue(this.donorData.towing_business_name)
      this.$refs.contactName.forceUpdateValue(this.donorData.towing_contact_name ? this.donorData.towing_contact_name
        : this.donorData.first_name + ' ' + this.donorData.last_name)
      this.$refs.pickupPhone.forceUpdateValue(this.donorData.towing_contact_phone ? this.donorData.towing_contact_phone
        : this.donorData.mobile_phone ? this.donorData.mobile_phone
          : this.donorData.home_phone ? this.donorData.home_phone
            : this.donorData.work_phone)
      this.$refs.pickupAddressStreet.forceUpdateValue(this.donorData.towing_address_different ? this.donorData.towing_address_street : this.donorData.address_street)
      this.$refs.pickupAddressCity.forceUpdateValue(this.donorData.towing_address_different ? this.donorData.towing_address_city : this.donorData.address_city)
      this.$refs.pickupAddressState.forceUpdateValue(this.donorData.towing_address_different ? this.donorData.towing_address_state : this.donorData.address_state)
      this.$refs.pickpuAddressZip.forceUpdateValue(this.donorData.towing_address_different ? this.donorData.towing_address_zip : this.donorData.address_zip)
    },
    selectedBuyer (buyer) {
      if (typeof buyer !== 'object') return
      if (buyer.preferred_towing_company_id) this.$refs.towingCompanyCombo.forceUpdateValue(buyer.preferred_towing_company_id)
    },
    copyBuyerAddress () {
      console.log(this.data.buyer_id)
      if (typeof this.data.buyer === 'object') {
        this.data.buyer_id = this.data.buyer.value
        this.$refs.destinationAddressStreet.forceUpdateValue(this.data.buyer.towing_address_street)
        this.$refs.destinationAddressCity.forceUpdateValue(this.data.buyer.towing_address_city)
        this.$refs.destinationAddressState.forceUpdateValue(this.data.buyer.towing_address_state)
        this.$refs.destinationAddressZip.forceUpdateValue(this.data.buyer.towing_address_zip)
      }
    },
    sendDonorVdr () {
      this.formatBuyerId()
      store.functions.vehicles.sendVdr(this.vehicleId,
        this.data,
        (errors) => {
          this.errors = {
            send_vdr: errors
          }
          this.hasMailErrors = true
        }).then((response) => {
        this.$toast.success('Donor VDR sent!')
        this.data.vdr_sent_date = response.data.vdr_sent_date
        this.hasMailErrors = false
        this.$emit('refresh')
      }).catch(() => {
        this.$toast.error('Failed to send Donor VDR...')
      })
    },
    downloadVdr () {
      this.formatBuyerId()
      store.functions.vehicles.downloadVdr(this.vehicleId,
        this.data,
        (errors) => {
          this.errors = errors
        }).then((response) => {
        helpers.openAsDownload(response.data, this.refNum + '_vdr.pdf')
      })
    },
    sendTowingRequest () {
      this.formatBuyerId()
      store.functions.vehicles.sendTowRequest(this.vehicleId,
        this.data,
        (errors) => {
          this.errors = {
            send_tow: errors
          }
          this.hasMailErrors = true
        }).then((response) => {
        this.$toast.success('Towing Request sent!')
        this.data.towing_request_sent_date = response.data.towing_request_sent_date
        this.hasMailErrors = false
        this.$emit('refresh')
      }).catch(() => {
        this.$toast.error('Failed to send Towing Request...')
      })
    },
    downloadTowingOrder () {
      this.formatBuyerId()
      store.functions.vehicles.downloadTowRequest(this.vehicleId,
        this.data,
        (errors) => {
          this.errors = errors
        }).then((response) => {
        helpers.openAsDownload(response.data, this.refNum + '_tow_request.pdf')
      })
    },
    sendBuyerVehicleInfo () {
      if (!this.data.buyer || this.isSendingToBuyer) return
      this.isSendingToBuyer = true
      this.formatBuyerId()
      store.functions.vehicles.sendBuyerVehicleInfo(this.vehicleId,
        this.data,
        (errors) => {
          this.sendBuyerErrors = errors
          this.isSendingToBuyer = false
        }).then((response) => {
        this.$toast.success('Buyer Info sent!')
        this.data.buyer_vehicle_info_sent_date = response.data.buyer_vehicle_info_sent_date
        this.hasErrors = false
        this.sendBuyerErrors = null
        this.isSendingToBuyer = false
        this.$emit('refresh')
      }).catch(() => {
        this.$toast.error('Failed to send Buyer Info...')
      })
    },
    submit () {
      this.formatBuyerId()
      this.saved = false
      store.functions.vehicles.updateVehicleProcessingInfo(this.vehicleId,
        this.data,
        (errors) => {
          this.errors = errors
          this.hasErrors = true
          this.saved = false
        }).then(() => {
        this.$toast.success('Changes saved.')
        this.hasErrors = false
        this.errors = {}
        this.saved = true
        this.$emit('refresh')
        setTimeout(() => {
          this.saved = false
        }, 500)
      }).catch(() => {
        this.$toast.error('Failed to save information...')
      })
    },
  },
  computed: {
    canSendTowRequest () {
      if (this.data.towing_company_id &&
              this.data.pickup_contact_name &&
              this.data.pickup_phone &&
              this.data.pickup_address_street &&
              this.data.pickup_address_city &&
              this.data.pickup_address_state &&
              this.data.pickup_address_zip &&
              this.data.destination_address_street &&
              this.data.destination_address_city &&
              this.data.destination_address_state &&
              this.data.destination_address_zip) {
        return true
      }
      return false
    },
    canSendVdr () {
      if (this.data.towing_company_id &&
              this.data.pickup_contact_name &&
              this.data.pickup_phone &&
              this.data.pickup_address_street &&
              this.data.pickup_address_city &&
              this.data.pickup_address_state &&
              this.data.actual_pickup) {
        return true
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
.fade-enter-active, .fade-leave-active {
    opacity: 1;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    transition: opacity 3s;
    opacity: 0;
}
.valuation-wrapper,
.buyer-wrapper {
  max-width: 900px;
  margin: 0 auto;
}
.valuation {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  margin-bottom: 2rem;
  h3 {
    width: 200px;
    margin: 0;
    align-self: flex-start;
  }
  dl {
    width: 150px;
    margin: 0;
  }
  form {
    display: flex;
    align-items: flex-start;
  }
  .field {
    width: 200px;
    margin: 0 3rem 0 0;
  }
}
.salvage-valuation {
  margin-bottom: 1rem;
  margin-left: 200px;
  align-items: flex-start;
  dl {
      width: 250px;
      margin: 0;
      .field {
          height: 4rem;
      }
  }
}
.vehicle-processing {
  h3 > a {
    font-weight: normal;
    font-size: .875rem;
    margin-left: 2rem;
  }
}
</style>

<style lang="scss">
  .salvage-valuation {
     input[type="date"] {
      align-items: flex-end;
      line-height: 1;
      font-size: .875rem;
      padding: 7px 0;
      -webkit-padding-after: 4px;
    }
    #caret-down {
      top: .5rem;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Vehicle/VehicleProcessing.vue